/**
 * Conversations manager popover.
 *
 * Anchored under the header [chat] button (bottom sheet on mobile). Grew from
 * a read-only "recent conversations" list into a Claude-Code-style manager:
 *
 *   - search across title / preview / customer / group
 *   - sections: ungrouped "recent" first, then GROUPS (collapsible; collapse
 *     state persists in localStorage)
 *   - per-row [⋯] action tray: rename · move to group · compact · delete
 *   - rename/group are direct client writes to andrei_sessions (rules allow
 *     the owner update); delete removes the session doc AND its messages
 *     (chunked batches); compact calls triggerAndreiCompactionHttp with the
 *     sessionId so a long thread can be summarised and continued.
 *
 * Clicking a row still opens Andrei with that session loaded.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection,
  doc,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { useAndreiChat } from '../../contexts/AndreiChatContext';
import { useAppData } from '../../providers/AppDataProvider';
import { authFetch } from '../../utils/cloudFunctionsAuth';
import { deleteConversation, parseFnResponse } from '../../utils/andreiConversations';

const COMPACT_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/triggerAndreiCompactionHttp';
const COLLAPSE_LS_KEY = 'andreiConvoGroupsCollapsed';
/** Ungrouped section key — not a legal user group name (leading space-less
 *  sentinel kept out of the group picker). */
const RECENT_KEY = '__recent__';

interface SessionRow {
  id: string;
  customerId?: string;
  jobId?: string;
  title?: string;
  group?: string;
  lastMessagePreview?: string;
  lastUpdatedAt?: { toDate?: () => Date } | null;
}

interface Props {
  show: boolean;
  onClose: () => void;
  /** DOM rect of the anchor element — popover positions relative to it. */
  anchorRect: DOMRect | null;
}

function formatRelative(d?: Date | null): string {
  if (!d) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d`;
  return d.toLocaleDateString();
}

function loadCollapsed(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(COLLAPSE_LS_KEY) || '{}') || {}; }
  catch { return {}; }
}

/** Small muted text-action button in the terminal idiom. `tall` bumps the
 *  hit target to the 44px touch standard on mobile. */
function ActionBtn({ label, onClick, disabled, danger, tall }: {
  label: string; onClick: () => void; disabled?: boolean; danger?: boolean; tall?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'transparent',
        border: '1px solid var(--bs-border-color, #2b3036)',
        borderRadius: 5,
        color: danger ? '#ef6a5a' : 'inherit',
        opacity: disabled ? 0.45 : 0.85,
        fontFamily: 'inherit',
        fontSize: tall ? 13 : 11,
        cursor: disabled ? 'default' : 'pointer',
        padding: '3px 10px',
        minHeight: tall ? 44 : 30,
      }}
    >
      {label}
    </button>
  );
}

export function RecentConversationsPopover({ show, onClose, anchorRect }: Props) {
  const { openAndreiChat, sessionId: activeSessionId, setSessionId } = useAndreiChat();
  const { customers } = useAppData();
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsed);
  // Per-row action tray + its sub-states.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [newGroupValue, setNewGroupValue] = useState('');
  const [busy, setBusy] = useState<{ id: string; kind: 'rename' | 'group' | 'compact' | 'delete' } | null>(null);
  // iOS keyboard inset for the mobile bottom sheet: fixed elements don't
  // track the visual viewport, so the sheet's lower half (where the tray
  // inputs live) ends up behind the keyboard without this ride-along.
  const [kbInset, setKbInset] = useState(0);
  const popRef = useRef<HTMLDivElement>(null);
  const anyBusy = busy !== null;

  /** Clear the busy slot only if this op still owns it — a concurrent op on
   *  another row must not have its indicator wiped by our finally. */
  const releaseBusy = (id: string, kind: 'rename' | 'group' | 'compact' | 'delete') => {
    setBusy((prev) => (prev && prev.id === id && prev.kind === kind ? null : prev));
  };

  useEffect(() => {
    if (!show) { setKbInset(0); return; }
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const update = () => {
      setKbInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [show]);

  // Subscribe to the user's 100 most recent sessions (was 10 as a read-only
  // jump list; a manager needs the real inventory). Grouping/search happen
  // client-side so the query — and its existing composite index — is
  // unchanged in shape.
  useEffect(() => {
    if (!show) return;
    const uid = getAuth().currentUser?.uid;
    if (!uid) { setSessions([]); setLoading(false); return; }
    setLoading(true);
    const db = getFirestore();
    const q = query(
      collection(db, 'andrei_sessions'),
      where('userUid', '==', uid),
      orderBy('lastUpdatedAt', 'desc'),
      limit(100),
    );
    setError(null);
    const unsub = onSnapshot(q, (snap) => {
      setSessions(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      setError(null);
      setLoading(false);
    }, (err) => {
      console.error('[RecentConversationsPopover] subscribe failed:', err);
      setError(err?.message || 'Could not load conversations.');
      setLoading(false);
    });
    return () => unsub();
  }, [show]);

  // Reset transient state whenever the popover closes.
  useEffect(() => {
    if (!show) { setExpandedId(null); setSearch(''); setNewGroupValue(''); }
  }, [show]);

  // Outside-click + Esc close.
  useEffect(() => {
    if (!show) return;
    const onDocClick = (e: MouseEvent) => {
      if (!popRef.current) return;
      if (popRef.current.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onOutside = (e: Event) => onDocClick(e as MouseEvent);
    // Defer attach by a frame so the click that opened us doesn't
    // immediately close it via this same listener. touchstart covers touch
    // devices (mousedown doesn't fire reliably before touchend on iOS).
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('touchstart', onOutside, { passive: true });
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [show, onClose]);

  const existingGroups = useMemo(() => {
    const set = new Set<string>();
    sessions.forEach((s) => { const g = (s.group || '').trim(); if (g) set.add(g); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [sessions]);

  // Search-filter, then partition into sections: ungrouped recents first,
  // then groups alphabetically. Rows inside every section stay newest-first
  // (the query ordering).
  const sections = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matches = (s: SessionRow) => {
      if (!needle) return true;
      const cust = s.customerId ? customers.find((c) => c.id === s.customerId) : null;
      const hay = [s.title, s.lastMessagePreview, s.group, cust?.name, cust?.company]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    };
    const filtered = sessions.filter(matches);
    const ungrouped = filtered.filter((s) => !(s.group || '').trim());
    const byGroup = new Map<string, SessionRow[]>();
    filtered.forEach((s) => {
      const g = (s.group || '').trim();
      if (!g) return;
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g)!.push(s);
    });
    const out: Array<{ key: string; label: string; rows: SessionRow[] }> = [];
    if (ungrouped.length > 0) out.push({ key: RECENT_KEY, label: 'recent', rows: ungrouped });
    // Group keys are namespaced ('g:' + name) so no user-typed group name can
    // ever collide with the RECENT_KEY sentinel (duplicate React keys +
    // shared collapse state).
    Array.from(byGroup.keys()).sort((a, b) => a.localeCompare(b))
      .forEach((g) => out.push({ key: `g:${g}`, label: g, rows: byGroup.get(g)! }));
    return out;
  }, [sessions, search, customers]);

  if (!show) return null;

  const toggleCollapse = (key: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(COLLAPSE_LS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const handleOpen = (s: SessionRow) => {
    const customer = s.customerId ? customers.find((c) => c.id === s.customerId) : null;
    openAndreiChat({
      sessionId: s.id,
      context: s.customerId
        ? { type: 'customer', id: s.customerId, label: customer?.name || customer?.company || s.customerId }
        : (s.jobId ? { type: 'job', id: s.jobId, label: `Job ${s.jobId}` } : null),
    });
    onClose();
  };

  const toggleTray = (s: SessionRow) => {
    if (expandedId === s.id) { setExpandedId(null); return; }
    setExpandedId(s.id);
    setRenameValue((s.title || '').trim());
    setNewGroupValue('');
  };

  const submitRename = async (s: SessionRow) => {
    if (anyBusy) return;
    const title = renameValue.trim();
    if (!title || title === (s.title || '').trim()) return;
    setBusy({ id: s.id, kind: 'rename' });
    try {
      await updateDoc(doc(getFirestore(), 'andrei_sessions', s.id), { title });
      toast.success('Conversation renamed');
    } catch (e: any) {
      toast.error(e?.message || 'Could not rename conversation');
    } finally {
      releaseBusy(s.id, 'rename');
    }
  };

  const assignGroup = async (s: SessionRow, group: string | null) => {
    if (anyBusy) return;
    if (group && /^__.*__$/.test(group)) { toast.error('That group name is reserved'); return; }
    setBusy({ id: s.id, kind: 'group' });
    try {
      await updateDoc(doc(getFirestore(), 'andrei_sessions', s.id), { group: group || null });
      toast.success(group ? `Moved to "${group}"` : 'Removed from group');
      setNewGroupValue('');
    } catch (e: any) {
      toast.error(e?.message || 'Could not update group');
    } finally {
      releaseBusy(s.id, 'group');
    }
  };

  const submitCompact = async (s: SessionRow) => {
    if (anyBusy) return;
    const title = (s.title || '').trim() || 'this conversation';
    if (!window.confirm(`Compact "${title}" now?\n\nOlder messages are summarised into a recap (originals are archived, not deleted) so the conversation can continue with room to breathe.`)) return;
    setBusy({ id: s.id, kind: 'compact' });
    try {
      const r = await authFetch(COMPACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: s.id }),
      });
      // Tolerant parse: an edge timeout/crash page is non-JSON — surface a
      // readable error instead of a SyntaxError from r.json().
      const j = await parseFnResponse(r);
      if (j.compacted) {
        toast.success(`Compacted — ${j.archivedCount} messages summarised into a recap.${j.degraded ? ' (summariser degraded — user messages kept verbatim)' : ''}`);
      } else if (j.reason === 'skipped_too_few_messages') {
        toast.info('Nothing to compact — the conversation is already short enough.');
      } else {
        toast.info('Compaction skipped.');
      }
    } catch (e: any) {
      toast.error(`Compaction failed: ${e?.message || 'error'}`);
    } finally {
      releaseBusy(s.id, 'compact');
    }
  };

  const submitDelete = async (s: SessionRow) => {
    if (anyBusy) return;
    const title = (s.title || '').trim() || 'this conversation';
    if (!window.confirm(`Delete "${title}"?\n\nThis permanently removes the conversation, all of its messages, and its compaction backups. It cannot be undone.`)) return;
    setBusy({ id: s.id, kind: 'delete' });
    try {
      // Server-side thorough delete: sweeps by sessionId alone (legacy
      // messages/recaps without userUid included) + the archive parts the
      // client is rules-locked out of.
      const r = await deleteConversation(s.id);
      setExpandedId(null);
      // If the deleted thread is the one loaded in the Andrei panel, drop
      // the panel back to a fresh session — otherwise the next send would
      // recreate the "deleted" session doc via the trigger and resurrect it.
      if (activeSessionId === s.id) setSessionId(null);
      toast.success(`Deleted conversation${r.deletedMessages ? ` + ${r.deletedMessages} messages` : ''}${activeSessionId === s.id ? ' — it was open, so the panel starts fresh' : ''}`);
    } catch (e: any) {
      toast.error(`Delete failed: ${e?.message || 'error'}`);
    } finally {
      releaseBusy(s.id, 'delete');
    }
  };

  // Mobile → bottom sheet (thumb-reachable, full-width) instead of a small
  // popover anchored to a header icon that may be off-screen after opening
  // from the overflow menu.
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;

  // Position: top-anchored under the [chat] button, right-aligned to
  // the button. Falls back to a fixed top-right corner if no anchor.
  const top = anchorRect ? anchorRect.bottom + 6 : 60;
  const right = anchorRect ? Math.max(8, window.innerWidth - anchorRect.right) : 12;

  const sheetStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed',
        // Ride above the iOS keyboard: fixed elements track the LAYOUT
        // viewport, so without this inset the sheet's lower half (tray
        // inputs) hides behind the keyboard.
        left: 0, right: 0, bottom: kbInset, top: 'auto',
        zIndex: 2000,
        background: 'var(--bs-body-bg, #0f1419)',
        borderTop: '1px solid var(--bs-border-color, #2b3036)',
        borderRadius: '14px 14px 0 0',
        width: '100%',
        // Keyboard open → shrink so the sheet's top stays on screen.
        maxHeight: kbInset > 0 ? `calc(100dvh - ${kbInset + 8}px)` : '78dvh',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.4)',
        padding: '6px 0 calc(6px + env(safe-area-inset-bottom))',
        fontFamily: 'inherit',
        fontSize: 15,   // ≥16px-ish rows — comfortable tap text on mobile
      }
    : {
        position: 'fixed',
        top,
        right,
        zIndex: 2000,
        background: 'var(--bs-body-bg, #0f1419)',
        border: '1px solid var(--bs-border-color, #2b3036)',
        borderRadius: 6,
        minWidth: 380,
        maxWidth: 'min(460px, calc(100vw - 16px))',
        maxHeight: '70vh',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        padding: '6px 0',
        fontFamily: 'inherit',
        fontSize: 12,
      };

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid var(--bs-border-color, #2b3036)',
    borderRadius: 5,
    color: 'inherit',
    fontFamily: 'inherit',
    fontSize: isMobile ? 16 : 12,  // 16px stops iOS zoom-on-focus
    padding: '5px 8px',
    outline: 'none',
    width: '100%',
  };

  return (
    <div
      ref={popRef}
      role="dialog"
      aria-label="Conversations"
      className="term-recent-conversations-popover"
      style={sheetStyle}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}>
        <span style={{ opacity: 0.6, fontSize: 11, flexShrink: 0 }}>conversations</span>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="search…"
          aria-label="Search conversations"
          style={{ ...inputStyle, flex: 1 }}
        />
      </div>
      {loading ? (
        <div style={{ padding: '12px', opacity: 0.6 }}>Loading…</div>
      ) : error ? (
        <div style={{ padding: '16px 12px', color: '#ef4444', fontSize: 12 }}>
          Couldn’t load conversations.<br />
          <span style={{ fontSize: 11, opacity: 0.7 }}>{error}</span>
        </div>
      ) : sections.length === 0 ? (
        <div style={{ padding: '16px 12px', opacity: 0.7 }}>
          {search.trim() ? 'No conversations match your search.' : (
            <>No conversations yet.<br />
            <span style={{ fontSize: 11, opacity: 0.7 }}>
              Open a customer and click <em>New conversation</em> to start one.
            </span></>
          )}
        </div>
      ) : (
        sections.map((section) => {
          const isCollapsed = !!collapsed[section.key];
          return (
            <div key={section.key}>
              <button
                type="button"
                onClick={() => toggleCollapse(section.key)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'rgba(255,255,255,0.03)',
                  border: 'none',
                  borderTop: '1px solid var(--bs-border-color, #2b3036)',
                  color: 'inherit',
                  padding: '5px 12px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 11,
                  opacity: 0.75,
                  display: 'flex',
                  justifyContent: 'space-between',
                  minHeight: isMobile ? 40 : undefined,
                  alignItems: 'center',
                }}
                aria-expanded={!isCollapsed}
              >
                <span>
                  <span style={{ display: 'inline-block', width: 12 }}>{isCollapsed ? '▸' : '▾'}</span>
                  {section.label}
                </span>
                <span style={{ opacity: 0.6 }}>{section.rows.length}</span>
              </button>
              {!isCollapsed && section.rows.map((s) => {
                const cust = s.customerId ? customers.find((c) => c.id === s.customerId) : null;
                const custName = cust?.name || cust?.company || (s.customerId ? 'Unknown' : 'No customer');
                const title = (s.title || '').trim() || '(new conversation)';
                const preview = (s.lastMessagePreview || '').trim();
                const date = s.lastUpdatedAt?.toDate ? s.lastUpdatedAt.toDate() : null;
                const isExpanded = expandedId === s.id;
                const rowBusy = busy?.id === s.id ? busy.kind : null;
                return (
                  <div key={s.id} style={{ borderTop: '1px solid var(--bs-border-color, #2b3036)' }}>
                    <div style={{ display: 'flex', alignItems: 'stretch' }}>
                      <button
                        type="button"
                        onClick={() => handleOpen(s)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          textAlign: 'left',
                          background: 'transparent',
                          border: 'none',
                          color: 'inherit',
                          padding: '8px 4px 8px 12px',
                          cursor: 'pointer',
                          display: 'block',
                          fontFamily: 'inherit',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                          <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {title}
                          </span>
                          <span style={{ opacity: 0.5, fontSize: 10, flexShrink: 0 }}>{formatRelative(date)}</span>
                        </div>
                        <div style={{ opacity: 0.6, fontSize: 11, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {custName}
                        </div>
                        {preview && (
                          <div
                            style={{
                              opacity: 0.55,
                              fontSize: 11,
                              marginTop: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {preview}
                          </div>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleTray(s)}
                        title="Conversation actions"
                        aria-label={`Actions for ${title}`}
                        aria-expanded={isExpanded}
                        style={{
                          flexShrink: 0,
                          width: 40,
                          background: isExpanded ? 'rgba(255,255,255,0.06)' : 'transparent',
                          border: 'none',
                          color: 'inherit',
                          opacity: 0.65,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: 15,
                        }}
                      >⋯</button>
                    </div>
                    {isExpanded && (
                      <div style={{ padding: '4px 12px 10px', display: 'flex', flexDirection: 'column', gap: 8, background: 'rgba(255,255,255,0.02)' }}>
                        {/* rename */}
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input
                            type="text"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !anyBusy) submitRename(s); }}
                            onFocus={(e) => e.currentTarget.scrollIntoView({ block: 'nearest' })}
                            disabled={anyBusy}
                            maxLength={120}
                            placeholder="conversation title"
                            aria-label="Rename conversation"
                            style={{ ...inputStyle, flex: 1 }}
                          />
                          <ActionBtn
                            tall={isMobile}
                            label={rowBusy === 'rename' ? 'saving…' : 'rename'}
                            disabled={anyBusy || !renameValue.trim() || renameValue.trim() === (s.title || '').trim()}
                            onClick={() => submitRename(s)}
                          />
                        </div>
                        {/* group */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          <span style={{ opacity: 0.55, fontSize: 11 }}>group:</span>
                          {existingGroups.map((g) => (
                            <ActionBtn
                              key={g}
                              tall={isMobile}
                              label={(s.group || '').trim() === g ? `● ${g}` : g}
                              disabled={anyBusy || (s.group || '').trim() === g}
                              onClick={() => assignGroup(s, g)}
                            />
                          ))}
                          {(s.group || '').trim() && (
                            <ActionBtn tall={isMobile} label="remove" disabled={anyBusy} onClick={() => assignGroup(s, null)} />
                          )}
                          <input
                            type="text"
                            value={newGroupValue}
                            onChange={(e) => setNewGroupValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !anyBusy && newGroupValue.trim()) assignGroup(s, newGroupValue.trim()); }}
                            onFocus={(e) => e.currentTarget.scrollIntoView({ block: 'nearest' })}
                            disabled={anyBusy}
                            maxLength={40}
                            placeholder="new group…"
                            aria-label="New group name"
                            style={{ ...inputStyle, width: 110, flex: '0 1 auto' }}
                          />
                          {newGroupValue.trim() && (
                            <ActionBtn
                              tall={isMobile}
                              label={rowBusy === 'group' ? '…' : 'move'}
                              disabled={anyBusy}
                              onClick={() => assignGroup(s, newGroupValue.trim())}
                            />
                          )}
                        </div>
                        {/* compact + delete */}
                        <div style={{ display: 'flex', gap: 6 }}>
                          <ActionBtn
                            tall={isMobile}
                            label={rowBusy === 'compact' ? 'compacting…' : 'compact history'}
                            disabled={anyBusy}
                            onClick={() => submitCompact(s)}
                          />
                          <ActionBtn
                            tall={isMobile}
                            label={rowBusy === 'delete' ? 'deleting…' : 'delete'}
                            danger
                            disabled={anyBusy}
                            onClick={() => submitDelete(s)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
}

export default RecentConversationsPopover;
